/**
 * Import text delivered by the Web Share Target / Android ACTION_SEND path.
 *
 * Exact Google Maps links retain their provider identity and source link.
 * Other shared text can become a new place search, with explicit selection.
 */

export interface SharedPlaceImport {
  query: string | null;
  /** True when a share arrived but contained no safe, human-readable text. */
  unsupportedOnly: boolean;
}

import { googleMapsLink } from './googleMaps';

const URL_TOKEN = /(?:https?:\/\/|www\.)[^\s<>{}\[\]]+/giu;
const GOOGLE_PLACE_ID = /\b(?:place_id\s*[:=]\s*)?ChIJ[A-Za-z0-9_-]{8,}\b/giu;
const GOOGLE_FTID = /\b(?:ftid\s*[:=]\s*)?0x[0-9a-f]+:0x[0-9a-f]+\b/giu;
const GOOGLE_DATA_TOKEN = /![1-9][a-z][^\s,;]+/giu;
const COORDINATE_PAIR =
  /(^|[\s([{"'])([-+]?\d{1,2}(?:\.\d+)?)(\s*[, ]\s*)([-+]?\d{1,3}(?:\.\d+)?)(?=$|[\s)\]}"',;])/gmu;
const DMS_COORDINATE =
  /\b\d{1,3}\s*°\s*\d{1,2}(?:\.\d+)?\s*['′]?\s*[NS]\s*[, ]+\s*\d{1,3}\s*°\s*\d{1,2}(?:\.\d+)?\s*['′]?\s*[EW]\b/giu;
const MAPS_BOILERPLATE = /^(?:shared\s+from\s+|view\s+(?:this\s+)?(?:place\s+)?(?:on\s+)?|open\s+in\s+)?google\s+maps[.!]?$/iu;
const GENERIC_LOCATION = /^(?:maps?|location|shared location|dropped pin)[.!]?$/iu;

function stripCoordinatePairs(text: string): string {
  return text.replace(COORDINATE_PAIR, (whole, prefix, latText, separator, lngText) => {
    // A bare "13 100 Sukhumvit Road" can be an address. A comma or decimal
    // makes the pair coordinate-shaped; valid ranges make the decision final.
    if (!separator.includes(',') && !latText.includes('.') && !lngText.includes('.')) return whole;
    const lat = Number(latText);
    const lng = Number(lngText);
    return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? prefix : whole;
  });
}

/** Sanitize share-sheet title/text into an independent provider-neutral query. */
export function cleanSharedPlaceText(parts: Array<string | null | undefined>): string | null {
  const lines: string[] = [];
  const seen = new Set<string>();

  for (const part of parts) {
    if (!part) continue;
    const cleanedPart = stripCoordinatePairs(
      part
      .replace(URL_TOKEN, ' ')
      .replace(GOOGLE_PLACE_ID, ' ')
      .replace(GOOGLE_FTID, ' ')
      .replace(GOOGLE_DATA_TOKEN, ' ')
      .replace(DMS_COORDINATE, ' '),
    );

    for (const rawLine of cleanedPart.split(/[\r\n]+/)) {
      const line = rawLine
        .replace(/^[\s|·•,;:\-–—]+|[\s|·•,;:\-–—]+$/gu, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      if (!line || MAPS_BOILERPLATE.test(line) || GENERIC_LOCATION.test(line)) continue;
      const key = line.toLocaleLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      lines.push(line);
    }
  }

  const query = lines.join(', ').replace(/(?:,\s*){2,}/g, ', ').trim().slice(0, 240);
  return query.length >= 2 ? query : null;
}

/** Read a one-shot `/share/` delivery. Google URLs retain their exact identity. */
export function readSharedPlaceImport(
  locationLike: Pick<Location, 'pathname' | 'search'> = window.location,
): SharedPlaceImport | null {
  if (!/^\/share\/?$/i.test(locationLike.pathname)) return null;
  const params = new URLSearchParams(locationLike.search);
  const title = params.get('title');
  const text = params.get('text');
  const url = params.get('url');
  const hadPayload = [title, text, url].some((value) => !!value?.trim());
  // Preserve an exact shared Google identity instead of replacing it with a
  // fuzzy geocoder search. Its destination remains a Google Maps source link.
  const query = googleMapsLink([url, text, title].filter(Boolean).join(' ')) ?? cleanSharedPlaceText([title, text]);
  return { query, unsupportedOnly: hadPayload && query === null };
}

/** Remove the shared payload from address bar/history before it can be reused. */
export function clearSharedPlaceImportUrl(): void {
  history.replaceState(history.state, '', '/');
}
